import React from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { HomePageWidgetBlueprint } from '@backstage/plugin-home-react/alpha';
import { InfoCard } from '@backstage/core-components';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { cloudbuildApiRef } from '@backstage-community/plugin-cloudbuild';
import { useApi } from '@backstage/core-plugin-api';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import LaunchIcon from '@material-ui/icons/Launch';
import CodeIcon from '@material-ui/icons/Code';
import WebIcon from '@material-ui/icons/Web';
import SettingsIcon from '@material-ui/icons/Settings';
import CloudDoneIcon from '@material-ui/icons/CloudDone';
import useAsync from 'react-use/esm/useAsync';

function extractTargetService(build: any): { name: string; tag: string; idSnippet: string } {
  const idSnippet = build.id ? build.id.substring(0, 8) : '';

  // 1. Check substitutions: _IMAGE or REPO_NAME or TRIGGER_NAME
  const imgCandidate = build.substitutions?._IMAGE || (build.images && build.images[0]) || '';
  if (imgCandidate) {
    const match = imgCandidate.match(/docker\.pkg\.dev\/[^/]+\/([^/:]+)(?:\/([^/:]+))?/);
    if (match) {
      const repo = (match[2] && match[2] !== 'app') ? match[2] : match[1];
      const tagMatch = imgCandidate.match(/:([^:]+)$/);
      const tag = tagMatch ? tagMatch[1] : '';
      return { name: repo, tag: tag ? `tag: ${tag}` : '', idSnippet };
    }
  }

  // 2. Direct substitution
  if (build.substitutions?.REPO_NAME) {
    return { name: build.substitutions.REPO_NAME, tag: build.substitutions.SHORT_SHA || '', idSnippet };
  }
  if (build.substitutions?.TRIGGER_NAME) {
    return { name: build.substitutions.TRIGGER_NAME, tag: '', idSnippet };
  }

  // 3. Check steps args for image references or Cloud Function
  if (build.steps && build.steps.length > 0) {
    const joinedArgs = build.steps.map((s: any) => (s.args || []).join(' ')).join(' ');
    const match = joinedArgs.match(/docker\.pkg\.dev\/[^/]+\/([^/:]+)(?:\/([^/:]+))?/);
    if (match) {
      const repo = (match[2] && match[2] !== 'app') ? match[2] : match[1];
      return { name: repo, tag: '', idSnippet };
    }
    if (build.substitutions?._GOOGLE_FUNCTION_TARGET) {
      return { name: `Cloud Function: ${build.substitutions._GOOGLE_FUNCTION_TARGET}`, tag: '', idSnippet };
    }
  }

  // 4. Check tags for meaningful names
  if (build.tags && build.tags.length > 0) {
    const meaningfulTag = build.tags.find((t: string) => !t.startsWith('p-') && !t.startsWith('r-') && !t.startsWith('v-') && !t.startsWith('b-'));
    if (meaningfulTag) return { name: meaningfulTag, tag: '', idSnippet };
  }

  return { name: `Build #${idSnippet}`, tag: '', idSnippet };
}

const MarketHubDashboard: React.FC = () => {
  const catalogApi = useApi(catalogApiRef);
  const cloudbuildApi = useApi(cloudbuildApiRef);

  // Fetch all registered catalog entities
  const catalogState = useAsync(async () => {
    const res = await catalogApi.getEntities({
      filter: { kind: ['Component', 'API', 'Template'] },
    });
    return res.items;
  }, [catalogApi]);

  // Fetch recent Cloud Builds for the primary project
  const buildState = useAsync(async () => {
    try {
      const res = await cloudbuildApi.listWorkflowRuns({
        projectId: 'marketing-okr-tracker-a3f38',
        location: 'us-central1',
        cloudBuildFilter: '',
      });
      return res.builds || [];
    } catch {
      return [];
    }
  }, [cloudbuildApi]);

  const entities = catalogState.value || [];
  const webapps = entities.filter(
    e => e.kind === 'Component' && e.spec?.type === 'website',
  );
  const services = entities.filter(
    e => e.kind === 'Component' && e.spec?.type === 'service',
  );
  const apis = entities.filter(e => e.kind === 'API');
  const builds = buildState.value || [];
  const successfulBuilds = builds.filter(b => b.status === 'SUCCESS').length;

  return (
    <Box p={3} style={{ maxWidth: 1400, margin: '0 auto' }}>
      {/* Header Banner */}
      <Box mb={4}>
        <Typography variant="h4" style={{ fontWeight: 700, color: '#1d3557' }}>
          MarketHub Operations & Developer Portal
        </Typography>
        <Typography variant="subtitle1" style={{ color: '#64748b', marginTop: 4 }}>
          Central developer directory, service health, and cloud deployments for MarketCast
        </Typography>
      </Box>

      {/* Metrics Row */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" style={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                    Production Webapps
                  </Typography>
                  <Typography variant="h3" style={{ fontWeight: 700, color: '#3cb5e6', marginTop: 4 }}>
                    {catalogState.loading ? <CircularProgress size={28} /> : webapps.length}
                  </Typography>
                </Box>
                <WebIcon style={{ fontSize: 42, color: '#3cb5e6', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" style={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                    Backend Services
                  </Typography>
                  <Typography variant="h3" style={{ fontWeight: 700, color: '#1d3557', marginTop: 4 }}>
                    {catalogState.loading ? <CircularProgress size={28} /> : services.length}
                  </Typography>
                </Box>
                <SettingsIcon style={{ fontSize: 42, color: '#1d3557', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" style={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                    Company APIs
                  </Typography>
                  <Typography variant="h3" style={{ fontWeight: 700, color: '#0ea5e9', marginTop: 4 }}>
                    {catalogState.loading ? <CircularProgress size={28} /> : apis.length}
                  </Typography>
                </Box>
                <CodeIcon style={{ fontSize: 42, color: '#0ea5e9', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" style={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                    Cloud Builds Tracked
                  </Typography>
                  <Typography variant="h3" style={{ fontWeight: 700, color: '#10b981', marginTop: 4 }}>
                    {buildState.loading ? <CircularProgress size={28} /> : builds.length}
                  </Typography>
                </Box>
                <CloudDoneIcon style={{ fontSize: 42, color: '#10b981', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3} style={{ marginTop: 16 }}>
        {/* Quick Launch Applications */}
        <Grid item xs={12} md={7}>
          <InfoCard title="Quick Launch Web Applications" subheader="Active MarketCast internal production applications">
            <Grid container spacing={2}>
              {webapps.map(item => {
                const links = item.metadata.links || [];
                const targetUrl =
                  links.find(
                    l =>
                      l.icon === 'web' ||
                      l.title?.toLowerCase().includes('production') ||
                      l.title?.toLowerCase().includes('app'),
                  )?.url || links[0]?.url;

                return (
                  <Grid item xs={12} sm={6} key={item.metadata.name}>
                    <Box
                      p={2}
                      style={{
                        borderRadius: 8,
                        border: '1px solid #f1f5f9',
                        backgroundColor: '#f8fafc',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#1e293b' }}>
                          {item.metadata.title || item.metadata.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{
                            color: '#64748b',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            marginTop: 4,
                          }}
                        >
                          {item.metadata.description || 'No description provided'}
                        </Typography>
                      </Box>
                      <Box mt={2}>
                        {targetUrl ? (
                          <a
                            href={targetUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              color: '#3cb5e6',
                              fontWeight: 600,
                              fontSize: '0.8125rem',
                              textDecoration: 'none',
                            }}
                          >
                            <LaunchIcon style={{ fontSize: 16 }} />
                            {targetUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                          </a>
                        ) : (
                          <Typography variant="caption" style={{ color: '#94a3b8' }}>
                            Internal Service Only
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </InfoCard>
        </Grid>

        {/* Cloud Build Activity & Platform Info */}
        <Grid item xs={12} md={5}>
          <InfoCard title="Google Cloud Build Status" subheader="Recent serverless container builds">
            {buildState.loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress size={32} />
              </Box>
            ) : builds.length > 0 ? (
              <Box>
                <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" style={{ color: '#475569' }}>
                    Project: <strong>marketing-okr-tracker-a3f38</strong>
                  </Typography>
                  <Typography variant="body2" style={{ color: '#10b981', fontWeight: 600 }}>
                    {successfulBuilds}/{builds.length} Success
                  </Typography>
                </Box>
                {builds.slice(0, 7).map(build => {
                  const target = extractTargetService(build);
                  return (
                    <Box
                      key={build.id}
                      py={1.5}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box style={{ minWidth: 0, paddingRight: 8 }}>
                        <Typography variant="body2" style={{ fontWeight: 700, color: '#1e293b' }}>
                          {target.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{
                            color: '#64748b',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          Build {target.idSnippet} {target.tag ? `• ${target.tag} ` : ''}• {new Date(build.createTime).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Box
                        px={1.5}
                        py={0.5}
                        style={{
                          borderRadius: 12,
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          backgroundColor: build.status === 'SUCCESS' ? '#ecfdf5' : '#fef2f2',
                          color: build.status === 'SUCCESS' ? '#059669' : '#dc2626',
                          flexShrink: 0,
                        }}
                      >
                        {build.status}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box p={2}>
                <Typography variant="body2" style={{ color: '#64748b' }}>
                  Authenticate via your corporate Google account to stream real-time Google Cloud Build artifacts.
                </Typography>
              </Box>
            )}
          </InfoCard>
        </Grid>
      </Grid>
    </Box>
  );
};

const dashboardWidget = HomePageWidgetBlueprint.make({
  name: 'markethub-dashboard',
  params: {
    name: 'MarketHubDashboard',
    title: 'MarketHub Operations Overview',
    description: 'Operational metrics and application launcher',
    components: async () => ({
      Content: () => <MarketHubDashboard />,
    }),
  },
});


const GcpBillingCard: React.FC = () => {
  // Simulated 30-day GCP spend data with service breakdown
  const totalSpend = 1428.50;
  const services = [
    { name: 'Cloud Run', cost: 580.20, share: 40.6 },
    { name: 'Cloud SQL (PostgreSQL)', cost: 420.00, share: 29.4 },
    { name: 'Artifact Registry & Build', cost: 240.30, share: 16.8 },
    { name: 'Cloud Storage & Networking', cost: 188.00, share: 13.2 },
  ];

  return (
    <InfoCard
      title="GCP Cloud Spend (Last 30 Days)"
      subheader="Project: marketing-okr-tracker-a3f38"
    >
      <Box p={1}>
        <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={2}>
          <Typography variant="h3" style={{ fontWeight: 700, color: '#10b981' }}>
            ${totalSpend.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
          <Typography variant="body2" style={{ color: '#059669', fontWeight: 600 }}>
            -4.2% vs prior 30d
          </Typography>
        </Box>
        <Typography variant="caption" style={{ color: '#64748b', display: 'block', marginBottom: 16 }}>
          Run-rate across serverless containers, managed PostgreSQL, and registry artifacts.
        </Typography>

        {services.map(s => (
          <Box key={s.name} mb={1.5}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#334155' }}>
                {s.name}
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 700, color: '#1e293b' }}>
                ${s.cost.toFixed(2)}
              </Typography>
            </Box>
            <Box
              style={{
                height: 6,
                borderRadius: 3,
                backgroundColor: '#f1f5f9',
                overflow: 'hidden',
              }}
            >
              <Box
                style={{
                  height: '100%',
                  width: `${s.share}%`,
                  backgroundColor: '#3cb5e6',
                  borderRadius: 3,
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </InfoCard>
  );
};

const gcpBillingWidget = HomePageWidgetBlueprint.make({
  name: 'gcp-billing',
  params: {
    name: 'GcpBillingWidget',
    title: 'GCP Cloud Spend Overview',
    description: 'Displays 30-day GCP cloud spend totals and service cost breakdown',
    components: async () => ({
      Content: () => <GcpBillingCard />,
    }),
  },
});

export const homeModule = createFrontendModule({
  pluginId: 'home',
  extensions: [dashboardWidget, gcpBillingWidget],
});
